import * as __typia_transform__isTypeUint32 from "typia/lib/internal/_isTypeUint32";
import typia, { type tags } from 'typia';
import { isEqual } from 'lodash';
import { TraversalPathMatchingType, type DirectedTripleWithFilters, type LoadedState, type Path, type TraversalLoadedState, type TraversalParams, } from '@ardoq/api-types';
import { hasTraversalPaths } from 'loadedState/isTraversalWithPaths';
import { rebuildTraversalLoadedState } from 'loadedState/rebuildTraversalLoadedState';
import { pathToKey } from 'viewpointBuilder/traversals/pathToKey';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import type { AiControlledCommand, CommandRejection, } from 'aiControlledCommands/types';
import { defineAiControlledCommand, type CommandValidationResult, } from 'aiControlledCommands/defineAiControlledCommand';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import { getChangeableTraversalDatasets, getViewpointName, type ViewpointsById, } from 'aiControlledCommands/viewpoint/traversalDatasets';
import { REQUIRED_PATHS_DESCRIPTION, WHEN_TO_USE } from './descriptions';
type RequiredStep = {
    /** Position of the path in the dataset's 'paths', starting at 0. */
    pathIndex: number & tags.Type<'uint32'>;
    /** Position of the step in that path, starting at 0. */
    stepIndex: number & tags.Type<'uint32'>;
};
export type DeclareRequiredPathsPayload = {
    /** The 'datasetId' of the dataset to change, copied from the context. */
    datasetId: string;
    /** The steps to make required. Steps that are already required may be included. */
    steps: RequiredStep[];
};
type StepDescriptor = {
    sourceType: string;
    referenceType: string;
    targetType: string;
    direction: DirectedTripleWithFilters['direction'];
    isRequired: boolean;
};
type DatasetDescriptor = {
    datasetId: string;
    viewpointName: string | null;
    paths: StepDescriptor[][];
};
export type RequiredPathsContext = {
    datasets: DatasetDescriptor[];
};
type ResolvedRequiredPaths = {
    loadedState: TraversalLoadedState;
    nextParams: TraversalParams;
};
const isRequiredTriple = ({ qualifier }: DirectedTripleWithFilters): boolean => qualifier === 'required';
const toStepDescriptor = (triple: DirectedTripleWithFilters): StepDescriptor => ({
    sourceType: triple.sourceType,
    referenceType: triple.referenceType,
    targetType: triple.targetType,
    direction: triple.direction,
    isRequired: isRequiredTriple(triple),
});
const getChangeableDatasets = (loadedStates: LoadedState[]): TraversalLoadedState[] => getChangeableTraversalDatasets(loadedStates).filter(({ data }) => hasTraversalPaths(data.paths));
const toDatasetDescriptor = (viewpointsById: ViewpointsById) => (loadedState: TraversalLoadedState): DatasetDescriptor => ({
    datasetId: loadedState.uuid,
    viewpointName: getViewpointName(viewpointsById, loadedState),
    paths: loadedState.data.paths.map(path => path.map(toStepDescriptor)),
});
export const hasRequirableDataset = (loadedStates: LoadedState[]): boolean => getChangeableDatasets(loadedStates).length > 0;
export const buildRequiredPathsCommand = (loadedStates: LoadedState[], viewpointsById: ViewpointsById): AiControlledCommand<typeof requiredPathsCommandDefinition> => ({
    ...requiredPathsCommandDefinition,
    context: {
        datasets: getChangeableDatasets(loadedStates).map(toDatasetDescriptor(viewpointsById)),
    },
});
const isStepInPaths = (paths: Path[]) => ({ pathIndex, stepIndex }: RequiredStep): boolean => stepIndex < (paths[pathIndex]?.length ?? 0);
const toStepKey = (paths: Path[]) => ({ pathIndex, stepIndex }: RequiredStep): string => pathToKey(paths[pathIndex].slice(0, stepIndex + 1));
const requireTriple = (triple: DirectedTripleWithFilters): DirectedTripleWithFilters => ({ ...triple, qualifier: 'required' });
const requireStepsInPath = (requiredStepKeys: ReadonlySet<string>) => (path: Path): Path => path.map((triple, index) => requiredStepKeys.has(pathToKey(path.slice(0, index + 1)))
    ? requireTriple(triple)
    : triple);
export const requireSteps = (paths: Path[], steps: RequiredStep[]): Path[] => paths.map(requireStepsInPath(new Set(steps.map(toStepKey(paths)))));
const toPathMatching = ({ paths, pathMatching }: TraversalParams, nextPaths: Path[]): TraversalPathMatchingType => {
    const hadRequired = paths.some(path => path.some(isRequiredTriple));
    const hasRequired = nextPaths.some(path => path.some(isRequiredTriple));
    return !hadRequired && hasRequired
        ? TraversalPathMatchingType.STRICT
        : pathMatching;
};
export const toRequiredPathsParams = (data: TraversalParams, steps: RequiredStep[]): TraversalParams => {
    const paths = requireSteps(data.paths, steps);
    return { ...data, paths, pathMatching: toPathMatching(data, paths) };
};
const reject = (reason: string): CommandRejection => ({
    status: 'rejected',
    reason,
});
const resolveRequiredPaths = ({ datasetId, steps }: DeclareRequiredPathsPayload, loadedStates: LoadedState[]): CommandValidationResult<ResolvedRequiredPaths> => {
    const loadedState = getChangeableDatasets(loadedStates).find(({ uuid }) => uuid === datasetId);
    if (!loadedState) {
        return reject("No open dataset matches this datasetId. Use a 'datasetId' from the context.");
    }
    if (steps.length === 0) {
        return reject('No steps were given. Ask the user which component type should be required.');
    }
    const { paths } = loadedState.data;
    if (!steps.every(isStepInPaths(paths))) {
        return reject("Some steps do not exist. Use a 'pathIndex' and 'stepIndex' from the dataset's 'paths'.");
    }
    return {
        status: 'valid',
        payload: {
            loadedState,
            nextParams: toRequiredPathsParams(loadedState.data, steps),
        },
    };
};
const executeRequiredPaths = async ({ loadedState, nextParams, }: ResolvedRequiredPaths): Promise<void> => {
    if (isEqual(loadedState.data, nextParams))
        return;
    const isRebuilt = await rebuildTraversalLoadedState(loadedState, nextParams);
    if (!isRebuilt) {
        throw new Error('Could not rebuild the dataset with the required steps.');
    }
};
const isPathApplied = (dataset: DatasetDescriptor) => (path: Path, pathIndex: number): boolean => path.every((triple, stepIndex) => !isRequiredTriple(triple) ||
    Boolean(dataset.paths[pathIndex]?.[stepIndex]?.isRequired));
export const requiredPathsCommandDefinition = defineAiControlledCommand<ResolvedRequiredPaths, RequiredPathsContext>({
    name: AiControlledCommandId.DECLARE_REQUIRED_PATHS,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                DeclareRequiredPathsPayload: {
                    type: "object",
                    properties: {
                        datasetId: {
                            type: "string",
                            description: "The 'datasetId' of the dataset to change, copied from the context."
                        },
                        steps: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/RequiredStep"
                            },
                            description: "The steps to make required. Steps that are already required may be included."
                        }
                    },
                    required: [
                        "datasetId",
                        "steps"
                    ]
                },
                RequiredStep: {
                    type: "object",
                    properties: {
                        pathIndex: {
                            type: "integer",
                            minimum: 0,
                            description: "Position of the path in the dataset's 'paths', starting at 0."
                        },
                        stepIndex: {
                            type: "integer",
                            minimum: 0,
                            description: "Position of the step in that path, starting at 0."
                        }
                    },
                    required: [
                        "pathIndex",
                        "stepIndex"
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/DeclareRequiredPathsPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: REQUIRED_PATHS_DESCRIPTION,
    isContext: (context): context is RequiredPathsContext => (() => { const _io0 = (input: any): boolean => Array.isArray(input.datasets) && input.datasets.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)); const _io1 = (input: any): boolean => "string" === typeof input.datasetId && (null === input.viewpointName || "string" === typeof input.viewpointName) && (Array.isArray(input.paths) && input.paths.every((elem: any) => Array.isArray(elem) && elem.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem)))); const _io2 = (input: any): boolean => "string" === typeof input.sourceType && "string" === typeof input.referenceType && "string" === typeof input.targetType && ("outgoing" === input.direction || "incoming" === input.direction) && "boolean" === typeof input.isRequired; return (input: any): input is RequiredPathsContext => "object" === typeof input && null !== input && _io0(input); })()(context),
    validate: (rawPayload, _context, { loadedStates }) => {
        if (!(() => { const _io0 = (input: any): boolean => "string" === typeof input.datasetId && (Array.isArray(input.steps) && input.steps.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem))); const _io1 = (input: any): boolean => "number" === typeof input.pathIndex && __typia_transform__isTypeUint32._isTypeUint32(input.pathIndex) && ("number" === typeof input.stepIndex && __typia_transform__isTypeUint32._isTypeUint32(input.stepIndex)); return (input: any): input is DeclareRequiredPathsPayload => "object" === typeof input && null !== input && _io0(input); })()(rawPayload)) {
            return reject('The AI returned invalid required steps.');
        }
        return resolveRequiredPaths(rawPayload, loadedStates);
    },
    execute: executeRequiredPaths,
    isApplied: ({ loadedState, nextParams }, { datasets }) => {
        const dataset = datasets.find(({ datasetId }) => datasetId === loadedState.uuid);
        if (!dataset)
            return false;
        return nextParams.paths.every(isPathApplied(dataset));
    },
});
